import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { AwsCredentialIdentity, AwsCredentialIdentityProvider } from '@smithy/types';
import { fetchAuthSession } from 'aws-amplify/auth';
import { inject, injectable } from 'inversify';
import { mqtt5, iot } from 'aws-iot-device-sdk-v2';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { authConfig, awsResources } from '../config/runtimeConfig';
import { Axios } from 'axios';

interface IoTSessionDescriptor {
    endpoint: string;
    region: string;
    allowedTopics: string[];
}

/**
 * Bridges the AWS SDK v3 `AwsCredentialIdentityProvider` to the synchronous
 * `CredentialsProvider` interface required by `aws-iot-device-sdk-v2`.
 *
 * The IoT SDK calls `getCredentials()` synchronously, so we eagerly fetch and
 * cache credentials before connecting, then refresh them on a timer.
 */
class CognitoCredentialsBridge {
    private cached?: AwsCredentialIdentity;
    private readonly provider: AwsCredentialIdentityProvider;
    private readonly region: string;
    private refreshPromise?: Promise<void>;

    constructor(provider: AwsCredentialIdentityProvider, region: string, refreshIntervalMs = 3 * 60 * 1000) {
        this.provider = provider;
        this.region = region;
        setInterval(() => { this.refreshCredentials().catch(console.error); }, refreshIntervalMs);
    }

    getCredentials() {
        return {
            aws_access_id: this.cached?.accessKeyId ?? '',
            aws_secret_key: this.cached?.secretAccessKey ?? '',
            aws_sts_token: this.cached?.sessionToken,
            aws_region: this.region,
        };
    }

    /** Must be called once before `getCredentials()` to warm the cache. */
    async refreshCredentials(): Promise<void> {
        if (!this.refreshPromise) {
            this.refreshPromise = this.provider()
                .then((credentials) => {
                    this.cached = credentials;
                })
                .finally(() => {
                    this.refreshPromise = undefined;
                });
        }

        await this.refreshPromise;
    }
}

/**
 * Injectable browser service that manages the IoT MQTT5 client lifecycle.
 *
 * Responsibilities:
 *  - Obtains Cognito Identity Pool credentials (same pattern as S3BrowserAccessService)
 *  - Fetches the IoT endpoint from the backend (centralized in IoTSessionService)
 *  - Creates and returns a connected `mqtt5.Mqtt5Client` (cached per session)
 *
 * Only one MQTT connection is created per service instance.
 */
@injectable()
export class IoTBrowserClientService {
    @inject('API') private api!: Axios;

    private credentialsProvider?: AwsCredentialIdentityProvider;
    private sessionDescriptor?: IoTSessionDescriptor;
    private mqttClient?: mqtt5.Mqtt5Client;
    /** Tracks whether the IoT policy has been attached for this session. */
    private policyAttached = false;

    private async buildCredentialsProvider(): Promise<AwsCredentialIdentityProvider> {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) {
            throw new Error('Cannot create IoT browser credentials without an authenticated Cognito session.');
        }
        const cognitoId = `cognito-idp.${authConfig.aws_cognito_region}.amazonaws.com/${authConfig.aws_user_pools_id}`;

        return fromCognitoIdentityPool({
            clientConfig: { region: authConfig.aws_cognito_region },
            identityPoolId: authConfig.aws_cognito_identity_pool_id,
            logins: { [cognitoId]: idToken },
        });
    }

    private async getCredentialsProvider(): Promise<AwsCredentialIdentityProvider> {
        if (!this.credentialsProvider) {
            this.credentialsProvider = await this.buildCredentialsProvider();
        }
        return this.credentialsProvider;
    }

    /**
     * Fetch the IoT session descriptor from the backend.
     *
     * The descriptor is returned by the backend's IoTSessionService and contains:
     * - endpoint: The IoT data ATS endpoint
     * - region: AWS region
     * - allowedTopics: Topics the user is allowed to access (informational)
     *
     * This centralizes IoT access control: the backend validates the user
     * before returning connection information.
     */
    private async fetchSessionDescriptor(): Promise<IoTSessionDescriptor> {
        if (this.sessionDescriptor) {
            return this.sessionDescriptor;
        }

        const response = await this.api.get<string>('iot/session');
        this.sessionDescriptor = JSON.parse(response.data);
        return this.sessionDescriptor!;
    }

    /**
     * Invoke the IoTPrincipalAttachHandler Lambda to attach the IoT policy to
     * the authenticated Cognito identity. Must run before the first MQTT connect.
     *
     * The call is skipped on subsequent invocations within the same service
     * instance (AWS IoT caches the attachment anyway, but this avoids the extra
     * round-trip). A new instance is created on every page load, so a fresh
     * attach happens once per session.
     *
     * Throws on any Lambda-level error so callers get an explicit failure
     * rather than a silent MQTT auth rejection.
     */
    private async ensurePolicyAttached(
        credentialsProvider: AwsCredentialIdentityProvider,
        region: string,
    ): Promise<void> {
        if (this.policyAttached) {
            return;
        }

        const authSession = await fetchAuthSession();
        const identityId: string | undefined = authSession.identityId;
        if (!identityId) {
            throw new Error('Could not determine Cognito identityId; cannot attach IoT policy.');
        }

        const lambdaClient = new LambdaClient({ credentials: credentialsProvider, region });
        const response = await lambdaClient.send(
            new InvokeCommand({
                FunctionName: awsResources.IoTAttachPrincipalLambdaName,
                Payload: new TextEncoder().encode(JSON.stringify({ identityId })),
            }),
        );

        const payloadText = response.Payload
            ? new TextDecoder().decode(response.Payload)
            : '';

        let payload: { statusCode?: number; body?: string } | undefined;
        if (payloadText) {
            payload = JSON.parse(payloadText) as { statusCode?: number; body?: string };
        }

        if (response.FunctionError) {
            throw new Error(
                `IoT policy attach Lambda returned an error: ${response.FunctionError}${payloadText ? ` (${payloadText})` : ''}`,
            );
        }

        const responseBody = payload?.body ? JSON.parse(payload.body) as { error?: string; message?: string } : undefined;
        if ((payload?.statusCode ?? 500) >= 400) {
            throw new Error(
                responseBody?.error
                ?? responseBody?.message
                ?? `IoT policy attach failed with status ${payload?.statusCode ?? 'unknown'}`,
            );
        }

        this.policyAttached = true;
    }

    /**
     * Returns a connected MQTT5 client. The client is created once and cached;
     * the same instance is returned on subsequent calls.
     */
    public async getClient(): Promise<mqtt5.Mqtt5Client> {
        if (this.mqttClient) {
            return this.mqttClient;
        }

        const credentialsProvider = await this.getCredentialsProvider();
        const sessionDescriptor = await this.fetchSessionDescriptor();
        const bridge = new CognitoCredentialsBridge(credentialsProvider, sessionDescriptor.region);

        // Warm the cache before building the client so getCredentials() returns
        // real values immediately on the first CONNECT attempt.
        await bridge.refreshCredentials();

        // Attach the IoT policy to this identity BEFORE opening the MQTT
        // connection. Without this, AWS IoT Core will reject the CONNECT.
        await this.ensurePolicyAttached(credentialsProvider, sessionDescriptor.region);

        const endpoint = sessionDescriptor.endpoint;

        const wsConfig: iot.WebsocketSigv4Config = {
            credentialsProvider: bridge,
            region: sessionDescriptor.region,
        };

        const builder = iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(
            endpoint,
            wsConfig,
        );

        return new Promise<mqtt5.Mqtt5Client>((resolve, reject) => {
            const client = new mqtt5.Mqtt5Client(builder.build());

            client.on('connectionSuccess', (_event: mqtt5.ConnectionSuccessEvent) => {
                this.mqttClient = client;
                resolve(client);
            });

            client.on('connectionFailure', (event: mqtt5.ConnectionFailureEvent) => {
                reject(event.error);
            });

            client.on('error', reject);

            client.on('disconnection', (event: mqtt5.DisconnectionEvent) => {
                console.warn('IoT MQTT disconnected:', event.error?.toString());
                // Clear the cached client so the next call to getClient() reconnects.
                if (this.mqttClient === client) {
                    this.mqttClient = undefined;
                }
            });

            client.on('stopped', () => {
                console.log('IoT MQTT client stopped.');
            });

            client.start();
        });
    }
}
